import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PAYMENT_MERCHANT } from '../config/paymentMerchant';
import {
  getBrowserDebugInfo,
  logUpiDebug,
  openUpiDeepLink,
  parsePaymentAmount,
  resolvePaymentUpiIntentLink,
  supportsUpiDeepLink,
} from '../utils/upiDeepLink';
import {
  getStoredPaymentAttemptSessionId,
  recordPaymentAttemptEvent,
} from '../utils/paymentAttemptApi';

/** Shared UPI deep-link launcher for mobile pay actions. */
export function useUpiPaymentLauncher({ displayAmount, planName }) {
  const [showNoAppHint, setShowNoAppHint] = useState(false);
  const [missingConfig, setMissingConfig] = useState(false);
  const [showUploadReminder, setShowUploadReminder] = useState(false);
  const launchedUpiRef = useRef(false);
  const sessionIdRef = useRef(getStoredPaymentAttemptSessionId());

  const supportsUpiPay = supportsUpiDeepLink();
  const amountValue = useMemo(() => parsePaymentAmount(displayAmount), [displayAmount]);
  const upiLink = useMemo(
    () => resolvePaymentUpiIntentLink({ displayAmount, planName }),
    [displayAmount, planName]
  );

  const canOpenUpi = Boolean(upiLink);

  useEffect(() => {
    sessionIdRef.current = getStoredPaymentAttemptSessionId();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !launchedUpiRef.current) return;
      launchedUpiRef.current = false;
      setShowUploadReminder(true);
      logUpiDebug('returned_to_payment_page', getBrowserDebugInfo());
      recordPaymentAttemptEvent(sessionIdRef.current, 'RETURNED_FROM_UPI');
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const openUpi = useCallback(
    (source = 'pay_with_upi_app') => {
      setShowNoAppHint(false);
      setMissingConfig(false);

      if (!supportsUpiDeepLink()) {
        logUpiDebug('pay_blocked_desktop', { source, ...getBrowserDebugInfo() });
        return;
      }

      if (!upiLink) {
        setMissingConfig(true);
        logUpiDebug('missing_upi_link', { displayAmount, source, ...getBrowserDebugInfo() });
        return;
      }

      logUpiDebug('upi_payment_button_clicked', {
        source,
        amount: amountValue,
        upiId: PAYMENT_MERCHANT.upiId,
        payeeName: PAYMENT_MERCHANT.payeeName,
        ...getBrowserDebugInfo(),
      });
      logUpiDebug('deep_link_generated', { url: upiLink });

      launchedUpiRef.current = true;
      recordPaymentAttemptEvent(sessionIdRef.current, 'UPI_APP_OPENED', { source });

      openUpiDeepLink(upiLink, {
        onMaybeUnavailable: () => {
          launchedUpiRef.current = false;
          setShowNoAppHint(true);
          recordPaymentAttemptEvent(sessionIdRef.current, 'PAYMENT_FAILED', {
            failure_stage: 'upi_launch',
            failure_reason: 'No UPI application responded on this device.',
          });
        },
      });
    },
    [amountValue, displayAmount, upiLink]
  );

  return {
    supportsUpiPay,
    isMobile: supportsUpiPay,
    canOpenUpi,
    openUpi,
    showNoAppHint,
    missingConfig,
    showUploadReminder,
    upiLink,
    paymentAttemptSessionId: sessionIdRef.current,
  };
}
